// The compact forward action that lives in the ribbon.
//
// It stands in for the step's `FlowCta` on the steps that have no pinned bar
// of their own (`navigation.placement: "ribbon"`), and on any step whose bar
// is off screen. Where the bar is pinned to the bottom of the viewport it is
// always visible, so this stays hidden — there is never more than one
// Continue on screen.
//
// Deliberately small: a count of what's left and an arrow. The full button,
// with its label and the "Next up" caption, is the bar's job.
import React from "react";
import { Box, Fade, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { jumpToField } from "./FlowCta";

const GOLD = "#C9A777";
const INK = "#3d322b";
const MUTED = "#7B6E66";

/**
 * @param {Object} props
 * @param {boolean} props.show
 * @param {string} props.label
 * @param {() => void} props.onClick
 * @param {boolean} [props.busy]
 * @param {boolean} [props.disabled]
 * @param {{done: number, total: number}} [props.progress]
 * @param {string} [props.nextIncomplete]
 */
const RibbonAction = ({ show, label, onClick, busy, disabled, progress, nextIncomplete }) => {
  const remaining = progress ? Math.max(progress.total - progress.done, 0) : 0;
  const complete = !progress || remaining === 0;

  return (
    <Fade in={show} timeout={160} unmountOnExit>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          pl: 1,
          ml: 0.5,
          borderLeft: "1px solid rgba(201,167,119,0.28)",
        }}
      >
        {progress && progress.total > 0 && (
          <Box
            component={nextIncomplete ? "button" : "span"}
            type={nextIncomplete ? "button" : undefined}
            onClick={nextIncomplete ? () => jumpToField(nextIncomplete) : undefined}
            aria-label={
              complete
                ? "All required fields complete"
                : `${remaining} to fill in — go to the next one`
            }
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              alignItems: "center",
              gap: 0.75,
              border: 0,
              p: 0,
              bgcolor: "transparent",
              fontFamily: "inherit",
              cursor: nextIncomplete ? "pointer" : "default",
              borderRadius: 999,
              "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: 3 },
            }}
          >
            {complete ? (
              <CheckRoundedIcon sx={{ fontSize: 16, color: "#5F9E6E" }} />
            ) : (
              <Typography
                component="span"
                sx={{
                  minWidth: 22,
                  height: 22,
                  px: 0.75,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: INK,
                  bgcolor: "rgba(201,167,119,0.18)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {remaining}
              </Typography>
            )}
            <Typography component="span" sx={{ color: MUTED, fontSize: "0.75rem", fontWeight: 600 }}>
              {complete ? "ready" : "left"}
            </Typography>
          </Box>
        )}

        <Tooltip title={label} arrow placement="bottom">
          <span>
            <IconButton
              onClick={onClick}
              disabled={disabled || busy}
              aria-label={label}
              size="small"
              sx={{
                width: 34,
                height: 34,
                color: "#fff",
                background: "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
                boxShadow: "0 6px 14px rgba(201,167,119,0.32)",
                transition: "transform .15s ease, box-shadow .2s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #C9A777 0%, #b4966b 50%, #a1885e 100%)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  background: "rgba(123,110,102,0.16)",
                  color: "rgba(123,110,102,0.55)",
                  boxShadow: "none",
                },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Fade>
  );
};

export default RibbonAction;
